import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Typography
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import contactService, { Contact, Segment } from '../services/contactService';
import { useUser } from '../contexts/UserContext';

interface ContactModalProps {
  open: boolean;
  onClose: () => void;
  onContactCreated: (contact: Contact) => void;
}

interface ContactFormData {
  first_name: string;
  last_name: string;
  email_address: string;
  phone: string;
  title: string;
  company: string;
  segments: number[];
}

const ContactModal: React.FC<ContactModalProps> = ({ open, onClose, onContactCreated }) => {
  const { selectedClientId } = useUser();
  const [formData, setFormData] = useState<ContactFormData>({
    first_name: '',
    last_name: '',
    email_address: '',
    phone: '',
    title: '',
    company: '',
    segments: []
  });
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Partial<ContactFormData>>({});

  const fetchSegments = useCallback(async () => {
    try {
      console.log('Fetching segments for client:', selectedClientId);
      const segmentsData = await contactService.getSegments(selectedClientId!);
      console.log('Segments received:', segmentsData);
      
      // Deduplicate segments by ID before setting state
      const uniqueSegments = Array.from(
        new Map(segmentsData.map(segment => [segment.id, segment])).values()
      );
      console.log('Unique segments after deduplication:', uniqueSegments);
      
      setSegments(uniqueSegments);
    } catch (err) {
      console.error('Error fetching segments:', err);
    }
  }, [selectedClientId]);

  useEffect(() => {
    if (open && selectedClientId) {
      fetchSegments();
    }
  }, [open, selectedClientId, fetchSegments]);

  const validateForm = (): boolean => {
    const errors: Partial<ContactFormData> = {};

    if (!formData.email_address.trim()) {
      errors.email_address = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address.trim())) {
      errors.email_address = 'Please enter a valid email address';
    }

    if (!formData.first_name.trim()) {
      errors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      errors.last_name = 'Last name is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: keyof ContactFormData, value: string | number[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSegmentToggle = (segmentId: number) => {
    setFormData(prev => ({
      ...prev,
      segments: prev.segments.includes(segmentId)
        ? prev.segments.filter(id => id !== segmentId)
        : [...prev.segments, segmentId]
    }));
  };

  const handleSubmit = async () => {
    if (!validateForm() || !selectedClientId) {
      console.error('Validation failed or no client selected:', { validateForm: validateForm(), selectedClientId });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const contactData = {
        client_id: selectedClientId,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email_address: formData.email_address.trim(),
        phone: formData.phone.trim() || undefined,
        title: formData.title.trim() || undefined,
        address: formData.company.trim() || undefined
      };

      console.log('ContactModal - About to create contact with data:', contactData);
      console.log('ContactModal - Segments:', formData.segments);

      // Create contact with segments
      const newContact = await contactService.createContact(contactData, {
        segment_ids: formData.segments.length > 0 ? formData.segments : undefined
      });
      
      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        email_address: '',
        phone: '',
        title: '',
        company: '',
        segments: []
      });
      
      onContactCreated(newContact);
      onClose();
    } catch (err: any) {
      console.error('ContactModal - Detailed error:', err);
      console.error('ContactModal - Error response:', err?.response?.data);
      
      if (err?.response?.status === 400) {
        setError(err.response.data.detail || 'Failed to create contact');
      } else if (err?.response?.status === 422) {
        // Handle validation errors
        const errorDetail = err.response.data.detail || 'Validation error';
        console.error('ContactModal - Validation error details:', JSON.stringify(errorDetail, null, 2));
        setError(`Validation error: ${JSON.stringify(errorDetail)}`);
      } else {
        setError('Failed to create contact. Please try again.');
      }
      console.error('Error creating contact:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        first_name: '',
        last_name: '',
        email_address: '',
        phone: '',
        title: '',
        company: '',
        segments: []
      });
      setError(null);
      setValidationErrors({});
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          minHeight: '600px'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 3, 
        pt: 3,
        px: 3,
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        minHeight: '64px'
      }}>
        <AddIcon color="primary" />
        <Typography variant="h6" component="span">
          Add New Contact
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 8, px: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: '1fr 1fr', mt: 2 }}>
          <TextField
            label="First Name *"
            value={formData.first_name}
            onChange={(e) => handleInputChange('first_name', e.target.value)}
            error={!!validationErrors.first_name}
            helperText={validationErrors.first_name}
            fullWidth
            size="medium"
          />

          <TextField
            label="Last Name *"
            value={formData.last_name}
            onChange={(e) => handleInputChange('last_name', e.target.value)}
            error={!!validationErrors.last_name}
            helperText={validationErrors.last_name}
            fullWidth
            size="medium"
          />

          <TextField
            label="Email Address *"
            type="email"
            value={formData.email_address}
            onChange={(e) => handleInputChange('email_address', e.target.value)}
            error={!!validationErrors.email_address}
            helperText={validationErrors.email_address}
            fullWidth
            size="medium"
            sx={{ gridColumn: '1 / -1' }}
          />

          <TextField
            label="Phone"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            fullWidth
            size="medium"
          />

          <TextField
            label="Title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            fullWidth
            size="medium"
          />

          <TextField
            label="Company"
            value={formData.company}
            onChange={(e) => handleInputChange('company', e.target.value)}
            fullWidth
            size="medium"
            sx={{ gridColumn: '1 / -1' }}
          />

          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
              Segments
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Array.from(new Map(segments.map(segment => [segment.id, segment])).values()).map((segment) => (
                <Chip
                  key={segment.id}
                  label={segment.name}
                  onClick={() => handleSegmentToggle(segment.id)}
                  variant={formData.segments.includes(segment.id) ? "filled" : "outlined"}
                  color={formData.segments.includes(segment.id) ? "primary" : "default"}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: formData.segments.includes(segment.id) 
                        ? 'primary.main' 
                        : 'action.hover'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || !formData.first_name.trim() || !formData.last_name.trim() || !formData.email_address.trim()}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {loading ? 'Creating...' : 'Create Contact'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactModal;
